import { useState } from "react";
import { useAiUsageLogs } from "@/hooks/useAiUsage";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

type LogStatus = "success" | "error" | "blocked";

const statusBadge: Record<LogStatus, { label: string; cls: string }> = {
  success: { label: "Başarılı", cls: "bg-emerald-100 text-emerald-700" },
  error: { label: "Hata", cls: "bg-red-100 text-red-700" },
  blocked: { label: "Engellendi", cls: "bg-amber-100 text-amber-700" },
};

function normalizeStatus(s: string): LogStatus {
  if (s === "error") return "error";
  if (s === "blocked") return "blocked";
  return "success";
}

export default function AiLoglar() {
  const [filter, setFilter] = useState<"all" | LogStatus>("all");
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const { data: logs, loading, error, reload } = useAiUsageLogs(200);

  const list = logs ?? [];
  const filtered = filter === "all" ? list : list.filter((l) => normalizeStatus(l.status) === filter);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {(["all", "success", "error", "blocked"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            {f === "all" ? "Tümü" : statusBadge[f].label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 flex items-center justify-center text-foreground-500">
          <i className="ri-loader-4-line text-xl animate-spin mr-2" aria-hidden="true"></i>
          <span className="text-sm">Loglar yükleniyor...</span>
        </div>
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 flex flex-col items-center justify-center text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-10 text-center text-foreground-500">
          <i className="ri-history-line text-2xl mb-2 block" aria-hidden="true"></i>
          <p className="text-sm">
            {list.length === 0
              ? `Henüz AI çağrı kaydı yok. Kullanıcılar ${name} Asistan'ı kullandıkça burada görünecek.`
              : "Bu filtreye uygun kayıt bulunamadı."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 text-left text-xs text-foreground-500">
                  <th className="px-4 py-3 font-medium">Kullanıcı</th>
                  <th className="px-4 py-3 font-medium">Model</th>
                  <th className="px-4 py-3 font-medium">Görev</th>
                  <th className="px-4 py-3 font-medium">Token</th>
                  <th className="px-4 py-3 font-medium">Süre</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                  <th className="px-4 py-3 font-medium">Saat</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l) => {
                  const badge = statusBadge[normalizeStatus(l.status)];
                  return (
                    <tr key={l.id} className="border-b border-background-100 last:border-0 hover:bg-background-100/50">
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{l.user}</td>
                      <td className="px-4 py-3 text-foreground-700 whitespace-nowrap">{l.model}</td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{l.taskType}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{l.tokens.toLocaleString("tr-TR")}</td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{(l.durationMs / 1000).toFixed(1)} sn</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{l.time}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}